/**
 * Studio module pages — thin presentation wrappers around existing
 * recipe / pricing / production components. NO duplicated business logic.
 *
 * The components themselves call the same backend endpoints; tenant_id is
 * carried by the JWT so studio users automatically get their own private
 * workspace data.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudioLayout, { StudioBackButton } from '../components/StudioLayout';
import IngredientsTab from '../components/recipes/IngredientsTab';
import RecipesTab from '../components/recipes/RecipesTab';
import PricingTab from '../components/recipes/PricingTab';
import ProductionPlannerPage from '../components/production/ProductionPlannerPage';
import { useAuth } from '../contexts/AuthContext';

const STUDIO_ROLE = 'studio_chef';

function StudioGuard({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/studio/login', { replace: true });
      return;
    }
    if (user.role !== STUDIO_ROLE) {
      // Wrong role landed here — push them to their normal home.
      navigate(user.role === 'manager' ? '/manager/dashboard' : '/admin/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);
  if (loading || !user || user.role !== STUDIO_ROLE) {
    return <div className="text-center py-20 text-sm text-[#7A2A12]">Loading…</div>;
  }
  return children;
}

export function StudioIngredientsPage() {
  return (
    <StudioGuard>
      <StudioLayout>
        <StudioBackButton />
        <div data-testid="studio-ingredients-page" className="bg-white/70 backdrop-blur-sm rounded-2xl border border-[#F4ECE0] shadow-sm p-3 sm:p-4">
          <div className="mb-3 px-1">
            <h2 className="text-lg font-bold text-[#3A1F12] tracking-tight">Ingredient Library</h2>
            <p className="text-[12px] text-[#7A5A4B] mt-0.5">
              The foundation for recipe costing, pricing, and production. Bulk import via Excel or add items inline.
            </p>
          </div>
          <IngredientsTab />
        </div>
      </StudioLayout>
    </StudioGuard>
  );
}

export function StudioRecipesPage() {
  const [isActive, setIsActive] = useState(false);
  useEffect(() => { setIsActive(true); }, []);
  return (
    <StudioGuard>
      <StudioLayout>
        <StudioBackButton />
        <div data-testid="studio-recipes-page" className="bg-white/70 backdrop-blur-sm rounded-2xl border border-[#F4ECE0] shadow-sm p-3 sm:p-4">
          <RecipesTab isActive={isActive} />
        </div>
      </StudioLayout>
    </StudioGuard>
  );
}

export function StudioPricingPage() {
  const [isActive, setIsActive] = useState(false);
  useEffect(() => { setIsActive(true); }, []);
  return (
    <StudioGuard>
      <StudioLayout>
        <StudioBackButton />
        <div data-testid="studio-pricing-page" className="bg-white/70 backdrop-blur-sm rounded-2xl border border-[#F4ECE0] shadow-sm p-3 sm:p-4">
          <PricingTab isActive={isActive} />
        </div>
      </StudioLayout>
    </StudioGuard>
  );
}

export function StudioProductionPage() {
  return (
    <StudioGuard>
      <StudioLayout>
        <StudioBackButton />
        <div data-testid="studio-production-page" className="bg-white/70 backdrop-blur-sm rounded-2xl border border-[#F4ECE0] shadow-sm p-3 sm:p-4">
          <ProductionPlannerPage />
        </div>
      </StudioLayout>
    </StudioGuard>
  );
}

export function StudioLibraryPage() {
  return (
    <StudioGuard>
      <StudioLayout>
        <StudioBackButton />
        <div data-testid="studio-library-page" className="text-center py-16 px-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-[#F4ECE0]">
          <h2 className="text-xl font-bold text-[#3A1F12]">Recipe Library — coming soon</h2>
          <p className="text-sm text-[#7A5A4B] mt-2 max-w-md mx-auto">
            Discover community recipes, clone them into your workspace, and share your own creations. Rolling out in Phase 2.
          </p>
        </div>
      </StudioLayout>
    </StudioGuard>
  );
}
